<template>
    <span class="progress-icon" :class="`progress-icon-${status}`">
        <svg viewBox="0 0 14 14" fill="none" role="img" :aria-label="label">
            <title>{{ label }}</title>
            <circle cx="7" cy="7" r="6" stroke="currentColor" stroke-width="1.5" :stroke-dasharray="status === 'not-started' ? '2.2 1.6' : undefined" />
            <path v-if="status === 'in-progress'" d="M7 3.5 A3.5 3.5 0 0 1 7 10.5 Z" fill="currentColor" />
            <template v-if="status === 'done'">
                <circle cx="7" cy="7" r="6" fill="currentColor" />
                <path d="M4.5 7.2 6.3 9 9.6 5.4" stroke="var(--color-ink-inverse)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
            </template>
        </svg>
    </span>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

// Linear-style progress mark from the performance-management prototype: a dashed ring when not
// started, a half-filled ring in progress, and a filled circle with a check when done.
export type ProgressStatus = "not-started" | "in-progress" | "done";

// `label` replaces the generic status name when the row knows more, such as "1 of 2 done".
const props = defineProps<{ status: ProgressStatus; label?: string }>();
const { t } = useI18n();

const label = computed(() => {
    if (props.label) {
        return props.label;
    }
    if (props.status === "done") {
        return t("chat.pmPicker.momentStatus.complete");
    }
    return props.status === "in-progress" ? t("chat.pmPicker.momentStatus.inProgress") : t("chat.pmPicker.momentStatus.notStarted");
});
</script>

<style scoped>
@layer components {
    .progress-icon {
        display: inline-flex;
        flex-shrink: 0;
        width: 14px;
        height: 14px;
    }

    .progress-icon svg {
        width: 100%;
        height: 100%;
    }

    .progress-icon-not-started {
        color: var(--color-ink-muted);
    }

    /* Progress and done share the green status ramp (DESIGN.md color ledger); the half fill versus the
       check tells them apart. Purple stays Nadia's. */
    .progress-icon-in-progress,
    .progress-icon-done {
        color: var(--color-status-success);
    }
}
</style>
